import { useState, useEffect, useRef, useMemo } from "react";
import { useBusiness } from "../business_context";
import { IFormField } from "@/api/businessLinesApi";
import { FiPlus, FiChevronDown, FiSave } from "react-icons/fi";
import { Dnd } from "./dnd";
import Dropdown from "./dropdown";
import toast from "react-hot-toast";

export default function FormTab() {
  const {
    selectedLineId,
    isDataLoading,
    defaultFormFields,
    isDefaultFieldsLoading,
    updateFormData,
    saveFormData,
    resetFormData,
    hasFormChanges,
    changedFormData,
    formData,
    isLineActive,
  } = useBusiness();
  const [formFields, setFormFields] = useState<IFormField[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Add ref to track if we're saving to prevent formFields update during save
  const isSavingRef = useRef(false);

  // Add ref to track previous formFields to prevent unnecessary updates
  const prevFormFieldsRef = useRef<string>("");

  // Store updateFormData in ref to avoid dependency issues
  const updateFormDataRef = useRef(updateFormData);
  updateFormDataRef.current = updateFormData;

  // Memoize current form data to prevent unnecessary re-renders
  const currentFormData = useMemo(() => {
    return changedFormData || formData;
  }, [changedFormData, formData]);

  // Simple initialization - only set formFields once when data is first loaded
  useEffect(() => {
    if (currentFormData && formFields.length === 0) {
      setFormFields(currentFormData);
    }
  }, [currentFormData]);

  // Update context when formFields change (simplified)
  useEffect(() => {
    if (formFields.length > 0 && !isSavingRef.current) {
      const formFieldsString = JSON.stringify(formFields);

      // Only update if formFields have actually changed
      if (formFieldsString !== prevFormFieldsRef.current) {
        updateFormDataRef.current(formFields);
        prevFormFieldsRef.current = formFieldsString;
      }
    }
  }, [formFields]);

  const handleSave = async () => {
    try {
      if (!selectedLineId) {
        toast.error("Please select a business line first");
        return;
      }

      // Set saving flag to prevent formFields update during save
      isSavingRef.current = true;

      // Update the context with current form fields
      updateFormData(formFields);

      // Use the context's save method
      await saveFormData();

      toast.success("Form fields saved successfully!");
    } catch (error) {
      console.error("FormTab: Error saving form fields:", error);
      toast.error("Failed to save form fields. Please try again.");
    } finally {
      // Clear saving flag after a short delay to allow effects to settle
      setTimeout(() => {
        isSavingRef.current = false;
      }, 100);
    }
  };

  // Show loading only if we're actually loading and don't have any data yet
  if (
    (isDataLoading || isDefaultFieldsLoading) &&
    !formFields.length &&
    !defaultFormFields.length
  ) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-gray-600">Loading form data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {!isLineActive && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <p className="text-yellow-800 font-medium">
              Read-only mode: This line is not active. You can only view form
              data.
            </p>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 mb-4">
        {hasFormChanges() && isLineActive && (
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 bg-[#78C7D1] text-white rounded-lg hover:bg-[#6ab4be]"
          >
            <FiSave size={20} />
            Save
          </button>
        )}
        {isLineActive && (
          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-2 px-4 py-2 bg-[#78C7D1] text-white rounded-lg hover:bg-[#6ab4be]"
            >
              <FiPlus size={20} />
              Add New
              <FiChevronDown size={20} />
            </button>

            {isDropdownOpen && (
              <Dropdown
                defaultFormFields={defaultFormFields}
                formFields={formFields}
                setFormFields={setFormFields}
                setIsDropdownOpen={setIsDropdownOpen}
              />
            )}
          </div>
        )}
      </div>
      <Dnd
        fields={formFields}
        setFields={isLineActive ? setFormFields : () => {}} // Disable field updates when not active
        activeId={activeId}
        setActiveId={isLineActive ? setActiveId : () => {}} // Disable active field changes when not active
        isReadOnly={!isLineActive} // Pass read-only flag to Dnd component
      />

      {/* Save and Cancel buttons */}
      {hasFormChanges() && isLineActive && (
        <div className="flex justify-center gap-4 mt-8">
          <button
            className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-8 rounded-full flex items-center gap-2 text-lg shadow-md"
            onClick={() => resetFormData()}
          >
            Cancel
          </button>
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-full flex items-center gap-2 text-lg shadow-md"
            onClick={handleSave}
          >
            <FiSave size={20} />
            Save Changes
          </button>
        </div>
      )}
    </div>
  );
}
